import { useState, useEffect, useCallback } from 'react'
import { Box, Typography, Icon, Divider } from '@mui/material'
import { format, addMonths, subMonths, differenceInDays } from 'date-fns'

import AdxFormModal from '../modal/AdxFormModal'
import CalendarBlock from './calendarBlock'
import AdxButton from 'components/custom/AdxButton'

const CalendarModal = ({
  calendarProps,
  openModal,
  setOpenModal,
  currentMonth,
  nextMonth,
  calendarValue,
  setCalendarValue,
  selectedFirst,
  setSelectedFirst,
  selectedSecond,
  setSelectedSecond,
  selectedDuration,
  setSelectedDuration,
  initialValue,
  setInitialValue,
  validateField,
}) => {
  const [clickState, setClickState] = useState('FIRST')
  const [selectedDisabledCounter, setSelectedDisabledCounter] = useState(0)

  const handleClose = async (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }

    setInitialValue({
      startDate: selectedFirst,
      endDate: selectedSecond,
      duration: selectedDuration,
    })

    calendarProps.setValue({
      startDate: format(selectedFirst, 'dd/MM/yyyy'),
      endDate: format(selectedSecond, 'dd/MM/yyyy'),
      duration: selectedDuration,
    })

    if (clickState === 'SECOND') {
      setClickState('FIRST')
    }

    setOpenModal(false)
  }

  const handleClick = (day) => {
    if (clickState === 'FIRST') {
      setClickState('SECOND')
      setSelectedFirst(day.object)
      setSelectedSecond('')
      setSelectedDuration(differenceInDays(day.object, day.object) + 1)
      setSelectedDisabledCounter(0)
    }

    if (clickState === 'SECOND') {
      setClickState('FIRST')
      if (differenceInDays(day.object, selectedFirst) < 1) {
        setSelectedFirst(day.object)
        setSelectedSecond(selectedFirst)
      } else {
        setSelectedSecond(day.object)
      }
    }
  }

  const handleSelectDuration = useCallback(() => {
    if (clickState === 'FIRST' && selectedFirst && selectedSecond) {
      setSelectedDuration(
        differenceInDays(selectedSecond, selectedFirst) + 1 - selectedDisabledCounter
      )
    }
  }, [clickState, selectedDisabledCounter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleSelectDuration()
  }, [handleSelectDuration])

  // THIS IS TO FIX INPUT STILL SHOW ERROR AFTER DATE IS SELECTED
  useEffect(() => {
    validateField('startDate')
  }, [validateField, openModal])

  return (
    <AdxFormModal
      open={openModal}
      onClose={handleClose}
      customHeader={
        <CalendarHeader
          selectedFirst={selectedFirst}
          selectedSecond={selectedSecond}
          setCalendarValue={setCalendarValue}
          calendarValue={calendarValue}
        />
      }
      enableFooter
      footerprops={
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: { xs: 'column', tablet: 'row' },
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: 'primary.light',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.875}>
            <Typography variant="body1" color="light-gray-text.main">
              Duration:
            </Typography>
            <Typography variant="body1" color="alternate.main">
              {`${selectedDuration} day(s)`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 1 }}>
            <AdxButton
              variant={'outlined'}
              onClick={() => {
                setSelectedFirst(initialValue.startDate)
                setSelectedSecond(initialValue.endDate)
                setSelectedDuration(initialValue.duration)
                setOpenModal(false)
              }}
              sx={{ width: { xs: 1, tablet: 'initial' } }}
            >
              Cancel
            </AdxButton>
            <AdxButton
              variant={'contained'}
              onClick={handleClose}
              disabled={!selectedFirst || !selectedSecond || clickState === 'SECOND'}
              sx={{ width: { xs: 1, tablet: '200px' } }}
            >
              Select
            </AdxButton>
          </Box>
        </Box>
      }
    >
      <Box
        width={1}
        display="flex"
        flexDirection={{ xs: 'column', tablet: 'row' }}
        justifyContent="space-between"
        minHeight={{ lg: '380px' }}
        columnGap={{ xs: 0, tablet: 5 }}
        rowGap={{ xs: 5, tablet: 0 }}
      >
        <Box
          sx={{
            width: { xs: 1, lg: 1 / 2 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CalendarBlock
            calendarProps={calendarProps}
            calendar={currentMonth}
            value={calendarValue}
            clickState={clickState}
            handleClick={handleClick}
            selectedFirst={selectedFirst}
            selectedSecond={selectedSecond}
            setSelectedDisabledCounter={setSelectedDisabledCounter}
          />
        </Box>
        <Box
          sx={{
            width: { xs: 1, lg: 1 / 2 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CalendarBlock
            calendarProps={calendarProps}
            calendar={nextMonth}
            value={addMonths(calendarValue, 1)}
            clickState={clickState}
            handleClick={handleClick}
            selectedFirst={selectedFirst}
            selectedSecond={selectedSecond}
            setSelectedDisabledCounter={setSelectedDisabledCounter}
          />
        </Box>
      </Box>
    </AdxFormModal>
  )
}

export default CalendarModal

// CALENDAR CUSTOM HEADER
const CalendarHeader = ({ selectedFirst, selectedSecond, setCalendarValue, calendarValue }) => {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', tablet: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 3,
      }}
    >
      {/* START & END DATE */}
      <Box
        sx={{
          backgroundColor: 'default-background.main',
          flex: 0.9,
          display: 'flex',
          flexDirection: { xs: 'column', tablet: 'row' },
          alignItems: { xs: 'start', tablet: 'center' },
          gap: { xs: 1, tablet: 3 },
          p: 1.25,
          borderRadius: '4.5px',
          width: '100%',
          border: '1px solid #E5E5E5',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Typography variant="body1" color="light-gray-text.main">
            Start:
          </Typography>
          <Typography
            variant="body1"
            color={selectedFirst ? 'dark-text.main' : 'light-gray-text-2.main'}
          >
            {selectedFirst !== '' ? format(selectedFirst, `eee, dd/MM/yyyy`) : 'DD/MM/YYYY'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Typography variant="body1" color="light-gray-text.main">
            End:
          </Typography>
          <Typography
            variant="body1"
            color={selectedSecond ? 'dark-text.main' : 'light-gray-text-2.main'}
          >
            {selectedSecond !== '' ? format(selectedSecond, `eee, dd/MM/yyyy`) : 'DD/MM/YYYY'}
          </Typography>
        </Box>
      </Box>

      {/* BUTTON: TODAY, PREV & NEXT */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: { xs: 'space-between', tablet: 'right' },
          width: { xs: '100%', tablet: 'initial' },
        }}
      >
        <AdxButton onClick={() => setCalendarValue(new Date())} variant={'outlined'}>
          Today
        </AdxButton>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <AdxButton
            onClick={() => setCalendarValue(subMonths(calendarValue, 1))}
            variant={'outlined-icon'}
          >
            <Icon>chevron_left</Icon>
          </AdxButton>

          <AdxButton
            onClick={() => setCalendarValue(addMonths(calendarValue, 1))}
            variant={'outlined-icon'}
          >
            <Icon>chevron_right</Icon>
          </AdxButton>
        </Box>
      </Box>
    </Box>
  )
}
